import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  writeBatch,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  buildUserComponentFromTemplate,
  componentLayoutWidths,
  defaultComponentTemplates,
  normalizeComponentTemplate,
} from '../data/components';
import { db } from '../firebase';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function instancesRef(uid) {
  return collection(db, 'userComponents', uid, 'instances');
}

function instanceRef(uid, componentId) {
  return doc(db, 'userComponents', uid, 'instances', componentId);
}

function timestampValue(value) {
  if (!value) {
    return 0;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  return Number(new Date(value)) || 0;
}

function sortComponents(items) {
  return [...items].sort((a, b) => {
    if ((a.position || 0) !== (b.position || 0)) {
      return (a.position || 0) - (b.position || 0);
    }
    return timestampValue(b.updatedAt || b.createdAt) - timestampValue(a.updatedAt || a.createdAt);
  });
}

async function isFriendWithOwner(ownerId, viewerUid) {
  if (!viewerUid || viewerUid === ownerId) {
    return false;
  }

  requireDb();
  const snapshot = await getDoc(doc(db, 'profileFriends', ownerId, 'friends', viewerUid));
  return snapshot.exists();
}

export async function listComponentTemplates(options = {}) {
  const { includeAdult = false } = options;
  const defaults = defaultComponentTemplates.map(normalizeComponentTemplate);

  if (!db) {
    return defaults.filter((template) => includeAdult || (!template.nsfw && !template.adult));
  }

  const snapshot = await getDocs(query(collection(db, 'componentTemplates'), where('published', '==', true)));
  const community = snapshot.docs.map((entry) => normalizeComponentTemplate({ id: entry.id, ...entry.data() }));
  return [...defaults, ...community].filter((template) => includeAdult || (!template.nsfw && !template.adult));
}

export async function listUserComponents(uid) {
  requireDb();
  const snapshot = await getDocs(instancesRef(uid));
  return sortComponents(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function listVisibleUserComponents(uid, viewerUid = null) {
  requireDb();
  if (viewerUid === uid) {
    return listUserComponents(uid);
  }

  const visibilityQuery = await isFriendWithOwner(uid, viewerUid)
    ? query(instancesRef(uid), where('visibility', 'in', ['public', 'friends']))
    : query(instancesRef(uid), where('visibility', '==', 'public'));
  const snapshot = await getDocs(visibilityQuery);
  return sortComponents(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function installComponentTemplate(uid, template, position = 0) {
  requireDb();
  const now = serverTimestamp();
  const component = buildUserComponentFromTemplate(template, uid, position);
  const ref = await addDoc(instancesRef(uid), {
    ...component,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateUserComponent(uid, componentId, component) {
  requireDb();
  await updateDoc(instanceRef(uid, componentId), {
    title: component.title.trim(),
    kind: component.kind,
    settings: component.settings || {},
    visibility: component.visibility,
    position: Number(component.position) || 0,
    placement: component.placement,
    layoutWidth: componentLayoutWidths.includes(component.layoutWidth) ? component.layoutWidth : 'half',
    dataSource: component.dataSource || 'manual',
    updatedAt: serverTimestamp(),
  });
}

function legacySectionToComponent(uid, section, profile, position) {
  if (section.id === 'hero' || section.type === 'hero') {
    return {
      ownerId: uid,
      templateId: 'hero-bio',
      title: section.title || 'Opening Page',
      kind: 'profile',
      settings: {
        tagline: profile.tagline || '',
        bio: profile.bio || '',
      },
      visibility: section.visibility || 'public',
      position,
      placement: 'profile',
      layoutWidth: 'full',
      dataSource: 'legacy-profile',
    };
  }

  if (section.id === 'stories' || section.type === 'stories') {
    return {
      ownerId: uid,
      templateId: 'story-list',
      title: section.title || 'Stories',
      kind: 'data-view',
      settings: { limit: 4 },
      visibility: section.visibility || 'public',
      position,
      placement: 'profile',
      layoutWidth: 'half',
      dataSource: 'stories',
    };
  }

  if (section.id === 'timeline' || section.type === 'timeline') {
    return {
      ownerId: uid,
      templateId: 'timeline-list',
      title: section.title || 'Timeline',
      kind: 'data-view',
      settings: { limit: 5 },
      visibility: section.visibility || 'public',
      position,
      placement: 'profile',
      layoutWidth: 'half',
      dataSource: 'timeline',
    };
  }

  if (section.id === 'widgets' || section.type === 'widgets') {
    return {
      ownerId: uid,
      templateId: 'legacy-widgets',
      title: section.title || 'Widgets',
      kind: 'data-view',
      settings: {},
      visibility: section.visibility || 'public',
      position,
      placement: 'profile',
      layoutWidth: 'full',
      dataSource: 'legacy-widgets',
    };
  }

  if (section.type === 'custom') {
    return {
      ownerId: uid,
      templateId: 'custom-markdown',
      title: section.title || 'Custom Section',
      kind: 'profile',
      settings: { content: section.content || '' },
      visibility: section.visibility || 'private',
      position,
      placement: 'profile',
      layoutWidth: 'half',
      dataSource: 'legacy-profile',
    };
  }

  return null;
}

export async function migrateLegacyProfileToComponents(uid, profile, widgets = []) {
  requireDb();
  const sections = Array.isArray(profile?.sections) ? profile.sections : [];
  const components = sections
    .map((section, index) => legacySectionToComponent(uid, section, profile, index))
    .filter(Boolean);

  if (!components.some((component) => component.templateId === 'hero-bio')) {
    components.unshift(legacySectionToComponent(uid, { id: 'hero', title: 'Opening Page', visibility: 'public' }, profile, 0));
  }

  if (widgets.length > 0 && !components.some((component) => component.templateId === 'legacy-widgets')) {
    components.push(legacySectionToComponent(uid, { id: 'widgets', title: 'Widgets', visibility: 'public' }, profile, components.length));
  }

  if (components.length === 0) {
    return 0;
  }

  const now = serverTimestamp();
  const batch = writeBatch(db);
  components.forEach((component, index) => {
    const ref = doc(instancesRef(uid));
    batch.set(ref, {
      ...component,
      position: index,
      createdAt: now,
      updatedAt: now,
    });
  });
  await batch.commit();
  return components.length;
}

export async function deleteUserComponent(uid, componentId) {
  requireDb();
  await deleteDoc(instanceRef(uid, componentId));
}

export async function publishComponentTemplate(uid, template) {
  requireDb();
  const normalized = normalizeComponentTemplate(template);
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'componentTemplates'), {
    creatorId: uid,
    name: normalized.name.trim(),
    description: normalized.description.trim(),
    kind: normalized.kind,
    category: normalized.category,
    schema: normalized.schema,
    defaultSettings: normalized.defaultSettings,
    defaultVisibility: normalized.defaultVisibility,
    defaultLayoutWidth: normalized.defaultLayoutWidth,
    published: true,
    nsfw: normalized.nsfw,
    adult: normalized.adult,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}
